import React, { createRef, Component, ReactNode, KeyboardEvent, MouseEvent } from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import { addClassToNode, removeClassFromNode } from '../Common/domNodeModifiers';
import { ModalRefContext, ModalContextProps } from './refContext';
const isTest = process.env.NODE_ENV === 'test';

import styles from './main.scss';

type ModalContainerProps = {
    isOpen: boolean;
    onClose: () => void;
    animates?: boolean;
    appendToBody: boolean;
    children?: ReactNode;
    closeOnEsc?: boolean;
    closeOnOutsideClick?: boolean;
    _INTERNAL_ONLY_CUSTOM_CONTENT_?: ReactNode;
    dataTn?: string;
    modalClass?: string;
    modalPosition: 'top' | 'center' | 'bottom';
    modalSize: 'default' | 'medium' | 'large' | 'fullscreen';
    onOpen?: () => void;
    overlayClass?: string;
    preventBackScroll: boolean;
    removeModalOnClose?: boolean;
    animateFromBottom?: boolean;
};

type ModalContainerState = {
    modalOpened: boolean;
    animation: null | 'opening' | 'closing';
};

export class ModalContainer extends Component<ModalContainerProps, ModalContainerState> {
    static defaultProps = {
        animates: true,
        closeOnEsc: true,
        closeOnOutsideClick: true,
        modalPosition: 'top',
        modalSize: 'default',
        appendToBody: !isTest,
        preventBackScroll: true,
        removeModalOnClose: false,
        animateFromBottom: false,
    };

    constructor(props: ModalContainerProps) {
        super(props);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.openModal = this.openModal.bind(this);
        this.removeModal = this.removeModal.bind(this);
        this.endCloseAnimation = this.endCloseAnimation.bind(this);
        this.handleAnimationEnd = this.handleAnimationEnd.bind(this);
        this.timeouts = [];
        this.scrollY = 0;
        this.state = {
            modalOpened: false,
            animation: null,
        };
        this.portal = null;
    }

    componentDidMount(): void {
        /* istanbul ignore else */
        if (this.props.isOpen && !this.state.modalOpened) {
            this.openModal();
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: ModalContainerProps): void {
        /* istanbul ignore else */
        if (nextProps.isOpen && !this.state.modalOpened) {
            this.openModal();
        } else if (!nextProps.isOpen && this.state.modalOpened) {
            this.closeModal();
        }
    }

    componentWillUnmount(): void {
        this.clearTimeouts();

        const { preventBackScroll } = this.props;

        this.removeModal();

        /* istanbul ignore else */
        if (preventBackScroll) {
            this.unlockScroll();
        }
    }

    clearTimeouts(): void {
        this.timeouts.forEach(clearTimeout);
    }

    modalRef: ModalContextProps = createRef();
    portal: HTMLDivElement | null;
    timeouts: NodeJS.Timeout[];
    scrollY: number;

    removeModal(): void {
        const { appendToBody } = this.props;
        if (appendToBody && document.body && this.portal) {
            document.body.removeChild(this.portal);
            this.portal = null;
        }
    }

    lockScroll(): void {
        this.scrollY = window.pageYOffset;
        addClassToNode({ domNode: document.body, className: styles.stopBodyScroll });
        // No need to scroll if position doesn't change - look at `.stopBodyScroll` styles
        if (this.scrollY === window.pageYOffset) {
            this.scrollY = 0;
        }
    }

    unlockScroll(): void {
        removeClassFromNode({ domNode: document.body, className: styles.stopBodyScroll });

        // Scroll back to previous position
        if (this.scrollY) {
            window.scrollTo(0, this.scrollY);
        }
    }

    handleAnimationEnd(): void {
        const { modalOpened, animation } = this.state;

        if (modalOpened && animation === 'opening' && this.props.preventBackScroll) {
            this.lockScroll();
        }
    }

    openModal(): void {
        const { closeOnEsc, preventBackScroll, animates, onOpen, appendToBody } = this.props;

        if (appendToBody) {
            if (!this.portal) {
                this.portal = document.createElement('div');
            }
            if (document.body) {
                document.body.appendChild(this.portal);
            }
        }

        /* istanbul ignore else */
        if (closeOnEsc) {
            /*
                using @ts-ignore otherwise getting the following error:
                Argument of type '(e: KeyboardEvent<Element>) => void' is not assignable to parameter of type 'EventListenerOrEventListenerObject'.
                    Type '(e: KeyboardEvent<Element>) => void' is not assignable to type 'EventListener'.
                Types of parameters 'e' and 'evt' are incompatible.
                    Type 'Event' is missing the following properties from type 'KeyboardEvent<Element>': altKey, charCode, ctrlKey, getModifierState, and 12 more.
            */
            // @ts-ignore
            document.addEventListener('keydown', this.handleKeydown);
        }

        if (preventBackScroll && !this.props.animates) {
            this.lockScroll();
        }

        if (onOpen) {
            onOpen();
        }

        if (animates) {
            this.setState(
                {
                    modalOpened: true,
                },
                () => {
                    this.timeouts.push(
                        setTimeout(() => this.setState({ animation: 'opening' }), 10)
                    );
                }
            );
        } else {
            this.setState({
                modalOpened: true,
            });
        }
    }

    endCloseAnimation(): void {
        this.setState({ modalOpened: false, animation: null });
        if (this.props.removeModalOnClose) {
            this.removeModal();
        }
    }

    closeModal(): void {
        const { closeOnEsc, preventBackScroll, animates, removeModalOnClose } = this.props;

        /* istanbul ignore else */
        if (closeOnEsc) {
            /*
                using @ts-ignore otherwise getting the following error:
                Argument of type '(e: KeyboardEvent<Element>) => void' is not assignable to parameter of type 'EventListenerOrEventListenerObject'.
                    Type '(e: KeyboardEvent<Element>) => void' is not assignable to type 'EventListener'.
                Types of parameters 'e' and 'evt' are incompatible.
                    Type 'Event' is missing the following properties from type 'KeyboardEvent<Element>': altKey, charCode, ctrlKey, getModifierState, and 12 more.
            */
            // @ts-ignore
            document.removeEventListener('keydown', this.handleKeydown);
        }

        if (preventBackScroll) {
            this.unlockScroll();
        }

        if (animates && this.state.modalOpened) {
            this.setState(
                {
                    animation: 'closing',
                },
                () => {
                    this.timeouts.push(setTimeout(this.endCloseAnimation, 300));
                }
            );
        } else {
            this.setState({
                modalOpened: false,
            });
            if (removeModalOnClose) {
                this.removeModal();
            }
        }
    }

    handleKeydown(e: KeyboardEvent): void {
        switch (e.key) {
            case 'Esc':
            case 'Escape':
                if (this.props.closeOnEsc) {
                    this.props.onClose();
                }
                break;
            default:
                break;
        }
    }

    handleOutsideClick(e: MouseEvent): void {
        const { closeOnOutsideClick, onClose } = this.props;
        e.stopPropagation();

        /* istanbul ignore else */
        if (closeOnOutsideClick) {
            onClose();
        }
    }

    handleInsideClick(e: MouseEvent): void {
        e.stopPropagation();
    }

    render(): React.ReactNode {
        const {
            animates,
            appendToBody,
            children,
            _INTERNAL_ONLY_CUSTOM_CONTENT_,
            dataTn,
            modalClass,
            modalPosition,
            modalSize,
            overlayClass,
            animateFromBottom,
        } = this.props;
        const { modalOpened, animation } = this.state;

        const overlayClasses = classnames(
            styles.overlayDefault,
            {
                [styles.overlayFadeStart]: animates,
                [styles.overlayDefaultOpening]: animates && animation === 'opening',
                [styles.overlayDefaultClosing]: animates && animation === 'closing',
                [styles.modalHidden]: !modalOpened,
            },
            overlayClass
        );

        let modalClasses = '';
        if (!_INTERNAL_ONLY_CUSTOM_CONTENT_) {
            const isModalPositionBottom = modalPosition === 'bottom';
            modalClasses = classnames(
                styles.modalDefault,
                {
                    [styles.modalMedium]: modalSize === 'medium',
                    [styles.modalLarge]: modalSize === 'large',
                    [styles.modalFullscreen]: modalSize === 'fullscreen',
                    [styles.modalHeightCenter]: modalPosition === 'center',
                    [styles.modalHeightBottomHidden]: isModalPositionBottom && animateFromBottom,
                    [styles.modalHeightBottomVisible]: isModalPositionBottom && !animateFromBottom,
                    [styles.animateFromBottom]:
                        isModalPositionBottom && animates && animation === 'opening',
                },
                modalClass
            );
        }

        const wrapperDataTn =
            (dataTn || 'modal-container') + '-' + (modalOpened ? 'open' : 'closed');
        const modalSelector = `modal-container-${modalOpened ? 'open' : 'closed'}`;

        const modal = (
            <div
                onClick={this.handleOutsideClick}
                id="ModalOverlay"
                data-tn={wrapperDataTn}
                data-modal-selector={modalSelector}
                className={overlayClasses}
                onTransitionEnd={this.handleAnimationEnd}
            >
                <div
                    id="Modal"
                    onClick={this.handleInsideClick}
                    className={modalClasses}
                    data-tn={dataTn}
                    ref={this.modalRef}
                >
                    <ModalRefContext.Provider value={this.modalRef}>
                        {_INTERNAL_ONLY_CUSTOM_CONTENT_ || children}
                    </ModalRefContext.Provider>
                </div>
            </div>
        );
        if (appendToBody && this.portal) {
            return ReactDOM.createPortal(modal, this.portal);
        }

        return modalOpened && modal;
    }
}
